import React from "react";
import { Button, TableCell, TableRow, Snackbar, Alert } from "@mui/material";
import moment from "moment";
import { Link } from "react-router-dom";

import { formatter } from "../../../../Modules/Common/commonUtils";

import { IOrderResponseItem } from "../../order.typings";
import { OrderItemMenu } from "./order-item-menu";
import { useOrderItemState } from "./order-item.state";
import DialogBox from "Modules/Common/dialogBox";
import { getOrderStatusColor } from "@utils/order-format.utils";
import { useMutation } from "@apollo/client";
import { MANUAL_CATERCASH_TRIGGER } from "../../../../Graphql/mutations";
import CircularProgress from "@mui/material/CircularProgress";
import { PaymentProviderBadge } from "@components/molecules/payment-provider-badge";
import { getOrderItemPresentation } from "./order-item.utils";
import "./order-item.css";
import "../../../group-order/group-order.css";

interface IOrderItemProps {
  item: IOrderResponseItem;
  userType: string;
  userId: string;
  tile?: string;
  orderState: any;
  onInvoiceClick(id: string): void;
  hasCaterCash?: boolean | undefined;
  showCustomerColumn?: boolean;
}

export function OrderItem({
  item,
  userId,
  userType,
  orderState,
  onInvoiceClick,
  hasCaterCash = undefined,
  showCustomerColumn = true,
}: IOrderItemProps) {
  const {
    isLoading,
    anchorEl,
    modalMode,
    onCloseModal,
    onCancelOrder,
    onDeleteOrder,
    onViewOrder,
    onEditOrder,
    onViewCustomer,
    onReOrder,
    onOpenMenuItem,
    onCloseMenu,
    onConfirmCancelOrder,
    onConfirmDeleteOrder,
    onContinueDraftOrder,
  } = useOrderItemState({
    orderState,
    userType,
    customerId: userId,
    order: item,
  });

  const [triggerCaterCash, { loading: caterCashLoading }] = useMutation(MANUAL_CATERCASH_TRIGGER);
  const [confirmTrigger, setConfirmTrigger] = React.useState(false);
  const [triggering, setTriggering] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean, message: string }>({ open: false, message: "" });
  const presentation = getOrderItemPresentation(
    item.orderStatus,
    item.isRefunded,
  );

  const onTriggerCaterCash = async () => {
    setTriggering(true);
    try {
      const res = await triggerCaterCash({ variables: { orderId: item._id } });
      if (res?.data?.manualCaterCashTrigger?.status === false) {
        setSnackbar({
          open: true,
          message: res.data.manualCaterCashTrigger.message || "CaterCash could not be issued.",
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: "An unexpected error occurred." });
    } finally {
      setTriggering(false);
      setConfirmTrigger(false);
    }
  };

  const onTableClick = () => {
    onViewOrder(item._id);
  };

  function onInvoice() {
    onInvoiceClick(item._id);
  }

  function onMenu(e: any) {
    if (isLoading) {
      return;
    }
    onOpenMenuItem(e);
  }

  return (
    <>
      <OrderItemMenu
        anchorEl={anchorEl}
        selectedItem={item}
        userType={userType}
        isLoading={isLoading}
        onClose={onCloseMenu}
        onViewOrder={onTableClick}
        onReOrder={onReOrder}
        onEditOrder={onEditOrder}
        onCancelOrder={onCancelOrder}
        onDeleteOrder={onDeleteOrder}
        onContinueOrder={onContinueDraftOrder}
      />
      {modalMode && (
        <DialogBox
          setOpenDialog={onCloseModal}
          handleDelete={modalMode === "delete" ? onConfirmDeleteOrder : onConfirmCancelOrder}
          openDialog={modalMode}
          title={modalMode === "delete" ? "Delete" : "Cancel"}
          discription={
            modalMode === "delete"
              ? presentation.isDraft
                ? "Are you sure you want to delete this draft order? This cannot be undone."
                : "Are you sure you want to delete this order?"
              : "Are you sure you want to cancel this order?"
          }
        />
      )}
      <TableRow
        className={presentation.rowClassName}
        style={{ cursor: "pointer" }}>
        <TableCell onClick={onTableClick}>
          {`#${item?.orderNo}`}
          {presentation.isDraft && (
            <span className="draft-order-marker">Draft order</span>
          )}
          {userType === "Admin" && item.groupOrderId && (
            <Link
              to={`/group-orders/${item.groupOrderId}/view`}
              className="group-order-marker"
              onClick={(e) => e.stopPropagation()}>
              Group
            </Link>
          )}
        </TableCell>
        <TableCell onClick={onTableClick}>
          {item?.orderDate
            ? `${moment(item?.orderDate).format("ddd, MMM DD YYYY")}`
            : "---"}
        </TableCell>
        <TableCell onClick={onTableClick}>
          {item?.hasDriver
            ? <Button className="status-btn" style={{ color: "#fff", backgroundColor: "#4CAF50" }} variant="contained">Yes</Button>
            : <Button className="status-btn" style={{ color: "#fff", backgroundColor: "#F44336" }} variant="contained">No</Button>
          }
        </TableCell>
        <TableCell onClick={onTableClick}>
          {item?.deliveryDate
            ? `${moment(item?.deliveryDate).format("ddd, MMM DD YYYY")}, ${item?.deliveryTime}`
            : "---"}
        </TableCell>
        <TableCell onClick={onTableClick}>
          {userType !== "Caterer" ? `${item?.catererDetails?.businessName}, ` : ""}
          {item.location ? `${item.location}, ` : ""}
          {/* TODO: replace locWithStreet with street in future */}
          {item.locWithStreet}
          {item.zip ? `, ${item.zip}` : ""}
        </TableCell>
        {showCustomerColumn && (
          <TableCell onClick={onTableClick}>
            {item?.customerDetails?.firstName} {item?.customerDetails?.lastName}
          </TableCell>
        )}
        <TableCell align="right" onClick={onTableClick}>
          {formatter.format(item?.grandTotal)}
        </TableCell>
        <TableCell align="center" onClick={onTableClick}>
          <Button
            className="status-btn"
            style={{
              color: "#fff",
              backgroundColor: getOrderStatusColor(item.orderStatus, item.isRefunded),
            }}
            variant="contained">
            {presentation.statusLabel}
          </Button>
        </TableCell>
        <TableCell align="center" onClick={onTableClick}>
          <PaymentProviderBadge order={item} size="small" />
        </TableCell>
        <TableCell align="center">
          {item?.orderStatus != "Draft" ? (
            <img
              onClick={onInvoice}
              src="../assets/images/pdf_icon.svg"
              className="img-fluid"
              alt="invoice img"
            />
          ) : (
            "--"
          )}
        </TableCell>
        <TableCell align="center">
          <img
            style={{ cursor: "pointer" }}
            onClick={onMenu}
            src="../assets/images/more-horizontal.svg"
            alt="img"
          />
        </TableCell>
        {userType === "Admin" &&
          <TableCell align="center">
            {hasCaterCash === true ? (
              <Button className="status-btn" style={{ color: "#fff", backgroundColor: "#4CAF50" }} variant="contained">
                Awarded
              </Button>
            ) : hasCaterCash === false ? (
              <Button
                className="status-btn"
                style={{ color: "#fff", backgroundColor: "#F44336" }}
                variant="contained"
                onClick={() => setConfirmTrigger(true)}
                disabled={triggering}
              >
                {triggering ? <CircularProgress size={16} color="inherit" /> : "Send Now"}
              </Button>
            ) : item?.orderStatus === "Completed" ? (
              <Button
                className="status-btn"
                style={{ color: "#fff", backgroundColor: "#F44336" }}
                variant="contained"
                onClick={() => setConfirmTrigger(true)}
                disabled={triggering}
              >
                {triggering ? <CircularProgress size={16} color="inherit" /> : "Send Now"}
              </Button>
            ) : (
              <Button className="status-btn" style={{ color: "#fff", backgroundColor: "#9E9E9E" }} variant="contained" disabled>
                Ineligible
              </Button>
            )}
          </TableCell>
        }
      </TableRow>
      <DialogBox
        setOpenDialog={() => setConfirmTrigger(false)}
        handleDelete={onTriggerCaterCash}
        openDialog={confirmTrigger ? "confirm" : ""}
        title="Trigger CaterCash"
        discription="Are you sure you want to issue CaterCash for this order?"
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity="warning" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
